import { __ } from '@wordpress/i18n';
import Button from './Button';

/**
 * Pagination component with numbered pages
 *
 * @param {Object} props
 * @param {number} props.currentPage - Current page number (1-indexed)
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.totalItems - Total number of items
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {boolean} props.isLoading - Whether data is loading
 */
const Pagination = ({
    currentPage = 1,
    totalPages = 1,
    totalItems = 0,
    onPageChange,
    isLoading = false
}) => {
    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            // Calculate start and end of visible range
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            // Adjust if at the beginning
            if (currentPage <= 2) {
                end = 4;
            }

            // Adjust if at the end
            if (currentPage >= totalPages - 1) {
                start = totalPages - 3;
            }

            // Add ellipsis before middle pages if needed
            if (start > 2) {
                pages.push('...');
            }

            // Add middle pages
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Add ellipsis after middle pages if needed
            if (end < totalPages - 1) {
                pages.push('...');
            }

            // Always show last page
            pages.push(totalPages);
        }

        return pages;
    };

    if (totalPages <= 1) {
        return (
            <div className="sb-pagination-info">
                <span className="sb-text-tiny">
                    {totalItems} {totalItems === 1 ? __('Item', 'sb-customizer') : __('Items', 'sb-customizer')}
                </span>
            </div>
        );
    }

    return (
        <div className="sb-pagination-wrapper">
            <div className="sb-pagination-info">
                <span className="sb-text-tiny">
                    {totalItems} {totalItems === 1 ? __('Item', 'sb-customizer') : __('Items', 'sb-customizer')}
                </span>
            </div>
            <div className="sb-pagination-controls">
                <Button
                    type="secondary"
                    size="small"
                    icon="chevron-left"
                    iconSize="7"
                    boxshadow="false"
                    disabled={currentPage === 1 || isLoading}
                    onClick={() => onPageChange(currentPage - 1)}
                    customClass="sb-pagination-btn sb-pagination-prev"
                />

                <div className="sb-pagination-pages">
                    {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                            <span key={`ellipsis-${index}`} className="sb-pagination-ellipsis">...</span>
                        ) : (
                            <button
                                key={page}
                                className={`sb-pagination-page ${currentPage === page ? 'sb-pagination-page-active' : ''}`}
                                onClick={() => onPageChange(page)}
                                disabled={isLoading}
                            >
                                {page}
                            </button>
                        )
                    ))}
                </div>

                <Button
                    type="secondary"
                    size="small"
                    icon="chevron-right"
                    iconSize="7"
                    boxshadow="false"
                    disabled={currentPage >= totalPages || isLoading}
                    onClick={() => onPageChange(currentPage + 1)}
                    customClass="sb-pagination-btn sb-pagination-next"
                />
            </div>
        </div>
    );
};

export default Pagination;
